<template>
  <div v-if="hasAccess" class="space-y-6 pb-10">
    <page-content>
      <section class="space-y-6">
        <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <dashboard-date-filters
            class="xl:flex-1"
            :quick-ranges="quickRanges"
            :active-range-id="activeQuickRangeId"
            :date-range="dateRange"
            :max-date="maxDate"
            @select-range="applyQuickRange"
            @show-full="showFull"
            @update:date-range="handleDateRangeUpdate"
          />

          <div class="tracking-filters inline-flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm xl:min-w-[360px]">
            <div class="tracking-filter-field tracking-filter-field--wide">
              <t-rich-select
                :key="domainSelectKey"
                class="tracking-filter-control"
                multiple
                :close-on-select="false"
                :options="domainOptions"
                value-attribute="uuid"
                text-attribute="label"
                placeholder="Tous les domaines"
                v-model="selectedDomainUuids"
              >
                <template slot="label" slot-scope="{ options, className }">
                  <span :class="className">
                    {{ domainSelectionLabel }}
                  </span>
                </template>
              </t-rich-select>
            </div>
            <employee-activity-export-button
              :date-range="dateRange"
              :selected-domain-uuids="selectedDomainUuids"
            />
            <newsletter-client-stats-export-button />
          </div>
        </div>

        <div class="employee-overview-grid">
          <section class="employee-space employee-space--downloads bg-white rounded-2xl shadow-sm border border-gray-200">
            <header class="employee-space-header">
              <div class="tracking-title-row">
                <h2 class="text-lg font-semibold text-gray-900">Téléchargements</h2>
                <button
                  type="button"
                  class="tracking-info-button"
                  title="Suivi mois par mois"
                  aria-label="Ouvrir le suivi mois par mois"
                  @click="openMonthlyTracking"
                >
                  +
                </button>
              </div>
              <div class="tracking-period-stats">
                <article class="tracking-period-stat tracking-period-stat--downloads">
                  <span>Sur la période</span>
                  <strong>{{ formatNumber(totals.downloadsPeriod) }}</strong>
                </article>
                <article class="tracking-period-stat tracking-period-stat--downloads">
                  <span>Total cumulé</span>
                  <strong>{{ formatNumber(totals.downloadsCumulative) }}</strong>
                </article>
              </div>
            </header>

            <div class="employee-card-body">
              <div class="employee-chart-area">
                <div class="tracking-chart-panel">
                  <chart-connection-trend
                    v-if="hasDownloadSeries"
                    class="h-full w-full"
                    :chart-data="downloadChartData"
                  />
                  <div v-else class="employee-empty-state">
                    Aucun téléchargement sur cette période.
                  </div>
                </div>
                <div class="employee-segmented-control" aria-label="Donnée du graphique téléchargements">
                  <button
                    v-for="option in downloadChartOptions"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-active': downloadChartMode === option.value }"
                    @click="downloadChartMode = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>

              <article class="employee-ring-card">
                <div class="employee-ring">
                  <svg viewBox="0 0 120 120" aria-hidden="true">
                    <circle class="employee-ring-bg" cx="60" cy="60" r="48" />
                    <circle
                      class="employee-ring-progress employee-ring-progress--downloads"
                      cx="60"
                      cy="60"
                      r="48"
                      :stroke-dasharray="ringCircumference"
                      :stroke-dashoffset="downloadRingStrokeOffset"
                    />
                  </svg>
                  <span>{{ formatPercent(downloadRingValue) }}</span>
                </div>
                <p>{{ downloadRingLabel }}</p>
                <div
                  v-if="downloadRingOptions.length > 1"
                  class="employee-segmented-control employee-segmented-control--compact"
                  aria-label="Formule téléchargements"
                >
                  <button
                    v-for="option in downloadRingOptions"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-active': downloadRingMode === option.value }"
                    @click="downloadRingMode = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </article>
            </div>
          </section>

          <section class="employee-space employee-space--active-users bg-white rounded-2xl shadow-sm border border-gray-200">
            <header class="employee-space-header">
              <div class="tracking-title-row">
                <h2 class="text-lg font-semibold text-gray-900">Utilisateurs actifs</h2>
              </div>
              <div class="tracking-period-stats">
                <article class="tracking-period-stat tracking-period-stat--active">
                  <span>Sur la période all</span>
                  <strong>{{ formatNumber(totals.activeLogins) }}</strong>
                </article>
                <article class="tracking-period-stat tracking-period-stat--active">
                  <span>Sur la période unique</span>
                  <strong>{{ formatNumber(totals.activeUsers) }}</strong>
                </article>
              </div>
            </header>

            <div class="employee-card-body">
              <div class="employee-chart-area">
                <div class="tracking-chart-panel">
                  <chart-connection-trend
                    v-if="hasActiveUsersSeries"
                    class="h-full w-full"
                    :chart-data="activeUsersChartData"
                  />
                  <div v-else class="employee-empty-state">
                    Aucun utilisateur actif sur cette période.
                  </div>
                </div>
                <div class="employee-segmented-control" aria-label="Donnée du graphique utilisateurs actifs">
                  <button
                    v-for="option in activeUsersChartOptions"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-active': activeUsersChartMode === option.value }"
                    @click="activeUsersChartMode = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>

              <article class="employee-ring-card">
                <div class="employee-ring">
                  <svg viewBox="0 0 120 120" aria-hidden="true">
                    <circle class="employee-ring-bg" cx="60" cy="60" r="48" />
                    <circle
                      class="employee-ring-progress employee-ring-progress--active"
                      cx="60"
                      cy="60"
                      r="48"
                      :stroke-dasharray="ringCircumference"
                      :stroke-dashoffset="activeUsersRingStrokeOffset"
                    />
                  </svg>
                  <span>{{ formatPercent(activeUsersRingValue) }}</span>
                </div>
                <p>{{ activeUsersRingLabel }}</p>
                <div
                  v-if="activeUsersRingOptions.length > 1"
                  class="employee-segmented-control employee-segmented-control--compact"
                  aria-label="Formule utilisateurs actifs"
                >
                  <button
                    v-for="option in activeUsersRingOptions"
                    :key="option.value"
                    type="button"
                    :class="{ 'is-active': activeUsersRingMode === option.value }"
                    @click="activeUsersRingMode = option.value"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </article>
            </div>
          </section>
        </div>

        <section class="rounded-2xl border border-gray-200 bg-white shadow-sm">
          <header class="border-b border-gray-200 px-5 py-4">
            <div class="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 class="text-lg font-semibold text-gray-900">Détail client</h2>
                <p class="mt-1 text-sm text-gray-500">
                  DL cumulés à fin de période, UA sur la période sélectionnée. Les pourcentages de la ligne total sont des moyennes clients.
                </p>
              </div>
              <p v-if="isLoading" class="text-sm text-gray-500">Chargement des données...</p>
            </div>
          </header>
          <div class="px-2 py-4">
            <m-v-p-table
              v-if="gridOptions"
              :options="gridOptions"
              :grid-data="gridData"
              status-bar-type="total"
              @gridReady="onGridReady"
            />
          </div>
        </section>

        <div
          v-if="isMonthlyTrackingOpen"
          class="tracking-modal-overlay"
          @click.self="closeMonthlyTracking"
        >
          <div class="tracking-modal-card tracking-modal-card--wide">
            <header class="tracking-modal-header">
              <div>
                <h3 class="tracking-modal-title">Suivi mois par mois</h3>
                <p class="tracking-modal-subtitle">
                  Téléchargements et utilisateurs actifs sur l'année sélectionnée.
                </p>
              </div>
              <div class="tracking-modal-domain-filter">
                <label>Client</label>
                <t-rich-select
                  :key="`${domainSelectKey}::monthly`"
                  class="tracking-filter-control"
                  multiple
                  :close-on-select="false"
                  :options="domainOptions"
                  value-attribute="uuid"
                  text-attribute="label"
                  placeholder="Tous les domaines"
                  v-model="selectedDomainUuids"
                >
                  <template slot="label" slot-scope="{ options, className }">
                    <span :class="className">
                      {{ domainSelectionLabel }}
                    </span>
                  </template>
                </t-rich-select>
              </div>
              <button
                type="button"
                class="tracking-modal-close"
                @click="closeMonthlyTracking"
              >
                ×
              </button>
            </header>

            <div class="tracking-modal-table-wrapper tracking-monthly-table-wrapper">
              <table class="tracking-modal-table tracking-monthly-table">
                <thead>
                  <tr>
                    <th>
                      <select v-model.number="selectedTrackingYear" class="tracking-year-select">
                        <option v-for="year in trackingYearOptions" :key="year" :value="year">
                          {{ year }}
                        </option>
                      </select>
                    </th>
                    <th v-for="month in monthlyTrackingRows" :key="month.key" class="is-right">
                      {{ month.label }}
                    </th>
                  </tr>
                </thead>
                <tbody v-if="!isMonthlyTrackingLoading">
                  <tr v-for="row in monthlyTrackingTableRows" :key="row.key">
                    <th>{{ row.label }}</th>
                    <td v-for="month in monthlyTrackingRows" :key="`${row.key}-${month.key}`" class="is-right">
                      {{ formatMonthlyValue(month[row.key], row.format) }}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="isMonthlyTrackingLoading" class="tracking-monthly-loader" aria-live="polite">
                <span class="tracking-monthly-spinner" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </page-content>
  </div>
  <div v-else class="px-6 py-10">
    <p class="text-sm text-gray-500">
      Vous n'avez pas accès à cette section.
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { debounce } from 'lodash'
import { http } from '@/services/api.service'
import { format, parseISO, subDays, subMonths, subWeeks } from 'date-fns'
import fr from 'date-fns/locale/fr'
import PageContent from '@/components/PageContent.vue'
import MVPTable from '@/components/agGrid/MVPTable.vue'
import DashboardDateFilters from '@/components/dashboard/DashboardDateFilters.vue'
import EmployeeActivityExportButton from '@/components/dashboard/EmployeeActivityExportButton.vue'
import NewsletterClientStatsExportButton from '@/components/dashboard/NewsletterClientStatsExportButton.vue'
import ChartConnectionTrend from '@/components/charts/ChartConnectionTrend.vue'
import { DASHBOARD_QUICK_RANGES } from './state'
import { getFullTrackingDateRange } from './domainPeriod'
import { fetchTrackingDomains } from './trackingDomains'
import { formatTrackingDomainSelectionLabel, getTrackingQueryState, normalizeTrackingDomainUuids, syncTrackingQueryFromVm } from './trackingQuery'

const DATE_FORMAT = 'yyyy-MM-dd'
const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']

function numericComparator (valueA, valueB) {
  return Number(valueA || 0) - Number(valueB || 0)
}

function toIsoDate (date) {
  return format(date, DATE_FORMAT)
}

function safeParseIsoDate (value) {
  if (!value || typeof value !== 'string') {
    return null
  }

  const isoDateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch
    return new Date(Number(year), Number(month) - 1, Number(day))
  }

  try {
    const parsed = parseISO(value)
    if (parsed instanceof Date && !Number.isNaN(parsed.getTime())) {
      return parsed
    }
  } catch (error) {
    //
  }

  return null
}

function compareDates (valueA, valueB) {
  const parsedA = safeParseIsoDate(valueA)
  const parsedB = safeParseIsoDate(valueB)

  if (!parsedA && !parsedB) return 0
  if (!parsedA) return 1
  if (!parsedB) return -1

  return parsedA.getTime() - parsedB.getTime()
}

function createDefaultTotals () {
  return {
    workforce: 0,
    downloadsPeriod: 0,
    downloadsCumulative: 0,
    downloadRateWorkforce: 0,
    downloadRateAverage: 0,
    activeLogins: 0,
    activeUsers: 0,
    activeUsersRowSum: 0,
    activeUsersRateDownloads: 0,
    activeUsersRateAverage: 0
  }
}

export default {
  name: 'app-dashboard-tracking-employees-general',
  components: {
    PageContent,
    MVPTable,
    DashboardDateFilters,
    EmployeeActivityExportButton,
    NewsletterClientStatsExportButton,
    ChartConnectionTrend
  },
  beforeRouteEnter (to, from, next) {
    next(async vm => {
      const hasAccess = await vm.ensureAccess()
      if (!hasAccess) {
        vm.$router.replace('/app/dashboard')
      }
    })
  },
  data () {
    const today = new Date()
    const defaultEnd = toIsoDate(today)
    const defaultStart = toIsoDate(subDays(today, 29))
    const trackingState = getTrackingQueryState(this.$route.query, {
      activeQuickRangeId: 'month-1',
      dateRange: [defaultStart, defaultEnd],
      selectedDomainUuids: []
    })

    return {
      quickRanges: DASHBOARD_QUICK_RANGES.map(range => ({ ...range })),
      activeQuickRangeId: trackingState.activeQuickRangeId,
      dateRange: trackingState.dateRange,
      maxDate: defaultEnd,
      domainOptions: [],
      selectedDomainUuids: trackingState.selectedDomainUuids,
      selectedTrackingYear: today.getFullYear(),
      downloadChartMode: 'value',
      downloadRingMode: 'workforce',
      activeUsersChartMode: 'value',
      activeUsersRingMode: 'downloads',
      totals: createDefaultTotals(),
      tableTotals: null,
      dailySeries: [],
      monthlySeries: [],
      gridOptions: null,
      gridData: [],
      gridApi: null,
      totalRowCount: 0,
      isLoading: false,
      isMonthlyTrackingLoading: false,
      isMonthlyTrackingOpen: false,
      statsRequestSequence: 0,
      monthlyRequestSequence: 0,
      activeStatsRequestKey: null,
      activeMonthlyRequestKey: null,
      debouncedLoadStats: null,
      debouncedLoadMonthlyTracking: null,
      isInitializingTracking: true
    }
  },
  computed: {
    ...mapGetters(['roles']),
    hasAccess () {
      return this.roles.includes('admin') || this.roles.includes('community-manager')
    },
    hasDomainFilter () {
      return normalizeTrackingDomainUuids(this.selectedDomainUuids).length > 0
    },
    domainSelectionLabel () {
      return formatTrackingDomainSelectionLabel(this.selectedDomainUuids, this.domainOptions)
    },
    domainSelectKey () {
      return `${this.$route.path}::${this.domainOptions.length}`
    },
    downloadChartOptions () {
      const options = [
        { value: 'value', label: 'Valeur' },
        { value: 'average', label: '%' },
        { value: 'workforce', label: '% effectif' }
      ]

      return this.hasDomainFilter
        ? options.filter(option => option.value !== 'workforce')
        : options
    },
    downloadRingOptions () {
      const options = [
        { value: 'workforce', label: 'Effectif' },
        { value: 'average', label: 'Moyenne' }
      ]

      return this.hasDomainFilter
        ? options.filter(option => option.value !== 'average')
        : options
    },
    activeUsersChartOptions () {
      const options = [
        { value: 'value', label: 'Valeur' },
        { value: 'average', label: '%' },
        { value: 'downloads', label: '% DL' }
      ]

      return this.hasDomainFilter
        ? options.filter(option => option.value !== 'downloads')
        : options
    },
    activeUsersRingOptions () {
      const options = [
        { value: 'downloads', label: 'DL' },
        { value: 'average', label: 'Moyenne' }
      ]

      return this.hasDomainFilter
        ? options.filter(option => option.value !== 'average')
        : options
    },
    normalizedDailySeries () {
      const rows = Array.isArray(this.dailySeries) ? this.dailySeries : []
      return rows.map(row => ({
        date: row?.date || '',
        downloads: Number(row?.downloads || 0),
        downloadRateWorkforce: Number(row?.downloadRateWorkforce || 0),
        downloadRateAverage: Number(row?.downloadRateAverage || 0),
        activeLogins: Number(row?.activeLogins || 0),
        activeUsers: Number(row?.activeUsers || 0),
        activeUsersRateDownloads: Number(row?.activeUsersRateDownloads || 0),
        activeUsersRateAverage: Number(row?.activeUsersRateAverage || 0)
      }))
    },
    hasDownloadSeries () {
      return this.normalizedDailySeries.some(row => row.downloads > 0 || row.downloadRateWorkforce > 0 || row.downloadRateAverage > 0)
    },
    hasActiveUsersSeries () {
      return this.normalizedDailySeries.some(row => row.activeUsers > 0 || row.activeUsersRateDownloads > 0 || row.activeUsersRateAverage > 0)
    },
    downloadChartConfig () {
      if (this.downloadChartMode === 'average') {
        return {
          label: 'Taux moyen de DL',
          key: 'downloadRateAverage',
          color: '#0f766e'
        }
      }

      if (this.downloadChartMode === 'workforce') {
        return {
          label: 'DL / effectif total',
          key: 'downloadRateWorkforce',
          color: '#7c3aed'
        }
      }

      return {
        label: 'Total DL cumulé',
        key: 'downloads',
        color: '#2563eb'
      }
    },
    activeUsersChartConfig () {
      if (this.activeUsersChartMode === 'average') {
        return {
          label: 'Taux moyen UA',
          key: 'activeUsersRateAverage',
          color: '#7c3aed'
        }
      }

      if (this.activeUsersChartMode === 'downloads') {
        return {
          label: 'UA / DL total',
          key: 'activeUsersRateDownloads',
          color: '#0f766e'
        }
      }

      return {
        label: 'UA uniques',
        key: 'activeUsers',
        color: '#2563eb'
      }
    },
    downloadChartData () {
      return this.buildLineChartData(this.downloadChartConfig)
    },
    activeUsersChartData () {
      return this.buildLineChartData(this.activeUsersChartConfig)
    },
    ringCircumference () {
      return 2 * Math.PI * 48
    },
    downloadRingValue () {
      return this.downloadRingMode === 'average'
        ? Number(this.totals.downloadRateAverage || 0)
        : Number(this.totals.downloadRateWorkforce || 0)
    },
    downloadRingLabel () {
      return this.downloadRingMode === 'average'
        ? 'Moyenne des clients actifs'
        : 'DL / effectif total'
    },
    downloadRingStrokeOffset () {
      return this.buildRingStrokeOffset(this.downloadRingValue)
    },
    activeUsersRingValue () {
      return this.activeUsersRingMode === 'average'
        ? Number(this.totals.activeUsersRateAverage || 0)
        : Number(this.totals.activeUsersRateDownloads || 0)
    },
    activeUsersRingLabel () {
      return this.activeUsersRingMode === 'average'
        ? 'Moyenne des clients actifs'
        : 'UA uniques / DL total'
    },
    activeUsersRingStrokeOffset () {
      return this.buildRingStrokeOffset(this.activeUsersRingValue)
    },
    trackingYearOptions () {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear; year >= 2020; year--) {
        years.push(year)
      }
      return years
    },
    monthlyTrackingRows () {
      const rows = Array.isArray(this.monthlySeries) ? this.monthlySeries : []
      return MONTH_LABELS.map((label, index) => {
        const monthNumber = index + 1
        const row = rows.find(item => Number(item.month) === monthNumber) || {}
        const isFutureMonth = this.isFutureTrackingMonth(monthNumber)

        return {
          key: monthNumber,
          label,
          downloads: Number(row.downloads || 0),
          downloadsCumulative: isFutureMonth ? 0 : Number(row.downloadsCumulative || 0),
          downloadRateWorkforce: isFutureMonth ? 0 : Number(row.downloadRateWorkforce || 0),
          downloadRateAverage: isFutureMonth ? 0 : Number(row.downloadRateAverage || 0),
          activeLogins: Number(row.activeLogins || 0),
          activeUsers: Number(row.activeUsers || 0),
          activeUsersRateDownloads: isFutureMonth ? 0 : Number(row.activeUsersRateDownloads || 0),
          activeUsersRateAverage: isFutureMonth ? 0 : Number(row.activeUsersRateAverage || 0)
        }
      })
    },
    monthlyTrackingTableRows () {
      const rows = [
        { key: 'downloadsCumulative', label: 'Total DL', format: 'number' },
        { key: 'downloads', label: 'Evolution DL', format: 'number' },
        { key: 'downloadRateWorkforce', label: '% DL (effectif)', format: 'percent' },
        { key: 'activeLogins', label: 'Connexions', format: 'number' },
        { key: 'activeUsers', label: 'Connexion unique', format: 'number' },
        { key: 'activeUsersRateDownloads', label: '% UA unique (DL)', format: 'percent' }
      ]

      if (!this.hasDomainFilter) {
        rows.splice(3, 0, { key: 'downloadRateAverage', label: '% DL (moyenne)', format: 'percent' })
        rows.push({ key: 'activeUsersRateAverage', label: '% UA unique (moyenne)', format: 'percent' })
      }

      return rows
    }
  },
  watch: {
    selectedDomainUuids: {
      handler () {
        if (this.activeQuickRangeId === 'full') {
          this.setFullDateRange()
        }
        syncTrackingQueryFromVm(this)
        this.syncAvailableModes()
        if (this.isInitializingTracking) {
          return
        }
        this.scheduleStatsReload()
        if (this.isMonthlyTrackingOpen) {
          this.scheduleMonthlyTrackingReload()
        }
      },
      deep: true
    },
    selectedTrackingYear () {
      if (!this.isInitializingTracking && this.isMonthlyTrackingOpen) {
        this.scheduleMonthlyTrackingReload()
      }
    }
  },
  async created () {
    this.initializeGridOptions()
    this.debouncedLoadStats = debounce(this.loadStats, 400)
    this.debouncedLoadMonthlyTracking = debounce(this.loadMonthlyTracking, 400)

    const hasAccess = await this.ensureAccess()
    if (!hasAccess) {
      this.$router.replace('/app/dashboard')
      return
    }

    const waitForDomains = this.activeQuickRangeId === 'full'
    const domainsPromise = this.fetchDomains()
    if (waitForDomains) {
      await domainsPromise
      this.setFullDateRange()
    }
    syncTrackingQueryFromVm(this)
    this.isInitializingTracking = false
    this.loadStats()
    if (this.isMonthlyTrackingOpen) {
      this.loadMonthlyTracking()
    }
    if (!waitForDomains) {
      await domainsPromise
    }
  },
  beforeDestroy () {
    if (this.debouncedLoadStats) {
      this.debouncedLoadStats.cancel()
    }
    if (this.debouncedLoadMonthlyTracking) {
      this.debouncedLoadMonthlyTracking.cancel()
    }
    this.statsRequestSequence += 1
    this.monthlyRequestSequence += 1
  },
  methods: {
    async ensureAccess () {
      if (!Array.isArray(this.roles) || this.roles.length === 0) {
        try {
          await this.$store.dispatch('FETCH_ROLES')
        } catch (error) {
          return false
        }
      }

      return this.hasAccess
    },
    initializeGridOptions () {
      this.gridOptions = {
        rowModelType: 'clientSide',
        suppressMultiSort: true,
        context: {
          componentParent: this
        },
        pinnedBottomRowData: [],
        getRowClass: params => params?.node?.rowPinned ? 'tracking-total-row' : '',
        defaultColDef: {
          resizable: true,
          sortable: true,
          floatingFilter: false,
          sortingOrder: ['asc', 'desc']
        },
        columnDefs: [
          {
            headerName: 'Domaine',
            field: 'domain_label',
            minWidth: 210,
            pinned: 'left',
            sortable: true
          },
          {
            headerName: 'Lancement',
            field: 'launch_date',
            minWidth: 130,
            sortable: true,
            comparator: compareDates,
            cellRenderer: params => params?.node?.rowPinned ? '' : this.formatDate(params.value),
            valueFormatter: params => params?.node?.rowPinned ? '' : this.formatDate(params.value)
          },
          {
            headerName: 'Effectif',
            field: 'workforce',
            minWidth: 120,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'DL',
            field: 'downloads',
            minWidth: 120,
            comparator: numericComparator,
            sort: 'desc',
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: '% DL',
            field: 'download_rate',
            minWidth: 120,
            comparator: numericComparator,
            valueFormatter: params => this.formatPercent(params.value)
          },
          {
            headerName: 'Connexions totales',
            field: 'active_logins',
            minWidth: 160,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: 'UA',
            field: 'active_users',
            minWidth: 120,
            comparator: numericComparator,
            valueFormatter: params => this.formatNumber(params.value)
          },
          {
            headerName: '% UA',
            field: 'active_users_rate',
            minWidth: 120,
            comparator: numericComparator,
            valueFormatter: params => this.formatPercent(params.value)
          }
        ]
      }
    },
    async fetchDomains () {
      try {
        const domains = await fetchTrackingDomains()
        this.domainOptions = domains

        const allowedUuids = new Set(domains.map(domain => domain.uuid))
        const currentSelectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
        const nextSelectedDomainUuids = currentSelectedDomainUuids
          .filter(uuid => allowedUuids.has(uuid))

        if (nextSelectedDomainUuids.join('|') !== currentSelectedDomainUuids.join('|')) {
          this.selectedDomainUuids = nextSelectedDomainUuids
        }
      } catch (error) {
        this.domainOptions = []
      }
    },
    scheduleStatsReload () {
      if (this.debouncedLoadStats) {
        this.debouncedLoadStats()
        return
      }
      this.loadStats()
    },
    scheduleMonthlyTrackingReload () {
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking()
        return
      }
      this.loadMonthlyTracking()
    },
    loadStats () {
      if (this.isInitializingTracking) {
        return
      }
      if (this.debouncedLoadStats) {
        this.debouncedLoadStats.cancel()
      }

      const params = this.buildRequestParams()
      const requestKey = JSON.stringify(params)
      if (this.activeStatsRequestKey === requestKey) {
        return
      }

      const requestSequence = this.statsRequestSequence + 1
      this.statsRequestSequence = requestSequence
      this.activeStatsRequestKey = requestKey
      this.isLoading = true

      http.get('/dashboard/employee-activity-general', { params })
        .then(({ data }) => {
          if (requestSequence !== this.statsRequestSequence) {
            return
          }

          this.totals = this.normalizeTotals(data?.totals)
          this.tableTotals = data?.tableTotals || null
          this.dailySeries = Array.isArray(data?.dailySeries) ? data.dailySeries : []
          this.gridData = Array.isArray(data?.domains) ? data.domains : []
          this.totalRowCount = this.gridData.length
          this.updatePinnedTotals()

          if (this.gridApi) {
            this.gridApi.dispatchEvent({ type: 'totalRowCountUpdated' })
          }
        })
        .catch(() => {
          if (requestSequence !== this.statsRequestSequence) {
            return
          }

          this.totals = createDefaultTotals()
          this.tableTotals = null
          this.dailySeries = []
          this.gridData = []
          this.totalRowCount = 0
          this.updatePinnedTotals()
          this.$buefy.toast.open({
            message: 'Impossible de charger les statistiques générales.',
            type: 'is-danger'
          })
        })
        .finally(() => {
          if (requestSequence === this.statsRequestSequence) {
            this.isLoading = false
            this.activeStatsRequestKey = null
          }
        })
    },
    loadMonthlyTracking () {
      if (this.isInitializingTracking) {
        return
      }
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }

      const params = {
        ...this.buildRequestParams(),
        monthlyOnly: 1
      }
      const requestKey = JSON.stringify(params)
      if (this.activeMonthlyRequestKey === requestKey) {
        return
      }

      const requestSequence = this.monthlyRequestSequence + 1
      this.monthlyRequestSequence = requestSequence
      this.activeMonthlyRequestKey = requestKey
      this.isMonthlyTrackingLoading = true

      http.get('/dashboard/employee-activity-general', { params })
        .then(({ data }) => {
          if (requestSequence !== this.monthlyRequestSequence) {
            return
          }
          this.monthlySeries = Array.isArray(data?.monthlySeries) ? data.monthlySeries : []
        })
        .catch(() => {
          if (requestSequence === this.monthlyRequestSequence) {
            this.monthlySeries = []
          }
        })
        .finally(() => {
          if (requestSequence === this.monthlyRequestSequence) {
            this.isMonthlyTrackingLoading = false
            this.activeMonthlyRequestKey = null
          }
        })
    },
    syncAvailableModes () {
      [
        { key: 'downloadChartMode', options: this.downloadChartOptions },
        { key: 'downloadRingMode', options: this.downloadRingOptions },
        { key: 'activeUsersChartMode', options: this.activeUsersChartOptions },
        { key: 'activeUsersRingMode', options: this.activeUsersRingOptions }
      ].forEach(({ key, options }) => {
        const values = options.map(option => option.value)
        if (!values.includes(this[key]) && values.length > 0) {
          this[key] = values[0]
        }
      })
    },
    normalizeTotals (totals) {
      const defaults = createDefaultTotals()
      return Object.keys(defaults).reduce((result, key) => {
        result[key] = Number(totals?.[key] || 0)
        return result
      }, {})
    },
    buildRequestParams () {
      const params = {
        trackingYear: this.selectedTrackingYear
      }

      if (Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        params.dateStart = this.dateRange[0]
        params.dateEnd = this.dateRange[1]
      }

      const selectedDomainUuids = normalizeTrackingDomainUuids(this.selectedDomainUuids)
      if (selectedDomainUuids.length > 0) {
        params.domains = selectedDomainUuids
      }

      return params
    },
    buildLineChartData (config) {
      const color = config.color
      return {
        labels: this.normalizedDailySeries.map(item => this.formatDateLabel(item.date)),
        datasets: [
          {
            label: config.label,
            data: this.normalizedDailySeries.map(item => Number(item[config.key] || 0)),
            borderColor: color,
            backgroundColor: this.hexToRgba(color, 0.12),
            borderWidth: this.normalizedDailySeries.length > 31 ? 0.75 : 1,
            pointBackgroundColor: color,
            pointRadius: this.normalizedDailySeries.length > 31 ? 0 : (this.normalizedDailySeries.length > 14 ? 1 : 1.5),
            pointHoverRadius: 2,
            pointHitRadius: 8,
            fill: false
          }
        ]
      }
    },
    buildRingStrokeOffset (value) {
      const progress = Math.min(100, Math.max(0, Number(value || 0)))
      return this.ringCircumference * (1 - (progress / 100))
    },
    updatePinnedTotals () {
      if (!this.gridApi) {
        return
      }

      const total = this.tableTotals
      this.gridApi.setPinnedBottomRowData(total ? [total] : [])
    },
    applyQuickRange (range) {
      if (!range) return

      this.activeQuickRangeId = range.id
      const today = new Date()
      let start = null

      if (range.type === 'week') {
        start = subWeeks(today, range.amount)
      } else if (range.type === 'month') {
        start = subMonths(today, range.amount)
      } else if (range.type === 'day') {
        start = subDays(today, range.amount - 1)
      }

      if (!start) {
        start = subDays(today, 29)
      }

      this.dateRange = [toIsoDate(start), toIsoDate(today)]
      syncTrackingQueryFromVm(this)
      this.loadStats()
    },
    showFull () {
      this.activeQuickRangeId = 'full'
      this.setFullDateRange()
      syncTrackingQueryFromVm(this)
      this.loadStats()
    },
    setFullDateRange () {
      this.dateRange = getFullTrackingDateRange(this.domainOptions, this.selectedDomainUuids)
      this.maxDate = this.dateRange[1]
    },
    handleDateRangeUpdate (range) {
      if (!Array.isArray(range) || range.length !== 2) {
        this.dateRange = null
      } else {
        this.dateRange = [...range]
      }

      this.activeQuickRangeId = null
      syncTrackingQueryFromVm(this)
      this.scheduleStatsReload()
    },
    onGridReady (params) {
      this.gridApi = params.api
      this.updatePinnedTotals()
      this.gridApi.dispatchEvent({ type: 'totalRowCountUpdated' })
    },
    formatDate (value) {
      if (!value) {
        return 'Non renseigné'
      }

      const parsed = safeParseIsoDate(value)
      if (!parsed) {
        return value
      }

      return format(parsed, 'dd/MM/yyyy', { locale: fr })
    },
    formatDateLabel (value) {
      const parsed = safeParseIsoDate(value)
      if (!parsed) {
        return value
      }

      return format(parsed, 'dd/MM', { locale: fr })
    },
    formatNumber (value) {
      return new Intl.NumberFormat('fr-FR').format(Number(value || 0))
    },
    formatPercent (value) {
      return `${new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(Number(value || 0))} %`
    },
    formatMonthlyValue (value, format) {
      if (format === 'percent') {
        return this.formatPercent(value)
      }

      return this.formatNumber(value)
    },
    isFutureTrackingMonth (monthNumber) {
      const today = new Date()
      const currentYear = today.getFullYear()
      const currentMonth = today.getMonth() + 1
      return this.selectedTrackingYear > currentYear ||
        (this.selectedTrackingYear === currentYear && Number(monthNumber) > currentMonth)
    },
    hexToRgba (hex, opacity) {
      const value = String(hex || '').replace('#', '')
      if (value.length !== 6) {
        return `rgba(37, 99, 235, ${opacity})`
      }

      const r = parseInt(value.slice(0, 2), 16)
      const g = parseInt(value.slice(2, 4), 16)
      const b = parseInt(value.slice(4, 6), 16)
      return `rgba(${r}, ${g}, ${b}, ${opacity})`
    },
    openMonthlyTracking () {
      this.isMonthlyTrackingOpen = true
      this.monthlySeries = []
      if (!this.isInitializingTracking) {
        this.loadMonthlyTracking()
      }
    },
    closeMonthlyTracking () {
      this.isMonthlyTrackingOpen = false
      if (this.debouncedLoadMonthlyTracking) {
        this.debouncedLoadMonthlyTracking.cancel()
      }
    }
  }
}
</script>

<style scoped>
.tracking-filters {
  width: auto;
  max-width: 100%;
}

.tracking-filter-field {
  min-width: 12rem;
  flex: 1 1 12rem;
  display: flex;
  align-items: center;
}

.tracking-filter-field--wide {
  min-width: 16rem;
  flex: 1.2 1 16rem;
}

.tracking-filter-control {
  width: 100%;
}

.employee-overview-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.5rem;
}

.employee-space {
  overflow: hidden;
}

.employee-space--downloads {
  border-top: 4px solid #2563eb;
}

.employee-space--active-users {
  border-top: 4px solid #0f766e;
}

.employee-space-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem;
  border-bottom: 1px solid #e5e7eb;
}

.tracking-title-row {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2rem;
}

.tracking-info-button {
  flex: 0 0 auto;
  width: 2rem;
  height: 2rem;
  border: 1px solid #cbd5e1;
  border-radius: 9999px;
  background: #ffffff;
  color: #1d4ed8;
  font-weight: 700;
  line-height: 1;
}

.tracking-info-button:hover {
  border-color: #2563eb;
  background: #eff6ff;
}

.tracking-period-stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
}

.tracking-period-stat {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 7.75rem;
  min-height: 2.9rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.5rem;
  background: #ffffff;
  padding: 0.4rem 0.7rem;
}

.tracking-period-stat span {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.1;
}

.tracking-period-stat strong {
  margin-top: 0.25rem;
  color: #111827;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.1;
}

.tracking-period-stat--downloads {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.tracking-period-stat--downloads span {
  color: #1d4ed8;
}

.tracking-period-stat--active {
  border-color: #99f6e4;
  background: #f0fdfa;
}

.tracking-period-stat--active span {
  color: #0f766e;
}

.employee-card-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(150px, 190px);
  gap: 1rem;
  align-items: center;
  padding: 1.25rem;
}

.employee-chart-area {
  min-width: 0;
}

.tracking-chart-panel {
  height: 19rem;
  min-width: 0;
  overflow: hidden;
}

.employee-empty-state {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 0.875rem;
}

.employee-ring-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  text-align: center;
}

.employee-ring {
  position: relative;
  width: 8rem;
  height: 8rem;
}

.employee-ring svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.employee-ring-bg,
.employee-ring-progress {
  fill: none;
  stroke-width: 12;
}

.employee-ring-bg {
  stroke: #e2e8f0;
}

.employee-ring-progress {
  stroke-linecap: round;
  transition: stroke-dashoffset 0.25s ease;
}

.employee-ring-progress--downloads {
  stroke: #2563eb;
}

.employee-ring-progress--active {
  stroke: #0f766e;
}

.employee-ring span {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #111827;
  font-size: 1.25rem;
  font-weight: 800;
}

.employee-ring-card p {
  margin: 0.5rem 0 0;
  color: #475569;
  font-size: 0.875rem;
}

.employee-segmented-control {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.85rem;
  border-radius: 9999px;
  border: 1px solid #dbeafe;
  background: #f8fafc;
  padding: 0.25rem;
}

.employee-segmented-control button {
  min-width: 4.5rem;
  border-radius: 9999px;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
  padding: 0.45rem 0.7rem;
  transition: background 0.15s ease, color 0.15s ease;
}

.employee-segmented-control button.is-active {
  background: #2563eb;
  color: #ffffff;
}

.employee-space--active-users .employee-segmented-control button.is-active,
.employee-segmented-control--compact button.is-active {
  background: #0f766e;
}

.employee-segmented-control--compact {
  justify-content: center;
  border-color: #ccfbf1;
}

.employee-segmented-control--compact button {
  min-width: 4rem;
}

.tracking-modal-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10020;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  min-height: 100vh;
  min-height: 100dvh;
  margin-top: 0 !important;
  padding: 1.5rem;
  overflow: auto;
  background: rgba(15, 23, 42, 0.45);
}

.tracking-modal-card {
  position: relative;
  z-index: 1;
  width: min(620px, 100%);
  max-height: min(520px, calc(100vh - 3rem));
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-radius: 1rem;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.22);
}

.tracking-modal-card--wide {
  width: min(1080px, 100%);
}

.tracking-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.25rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.tracking-modal-domain-filter {
  width: min(22rem, 45%);
  margin-left: auto;
}

.tracking-modal-domain-filter label {
  display: block;
  margin-bottom: 0.35rem;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 700;
}

.tracking-year-select {
  min-width: 6rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: #fff;
  color: #111827;
  font-size: 0.875rem;
  padding: 0.4rem 0.65rem;
}

.tracking-modal-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
}

.tracking-modal-subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  color: #6b7280;
}

.tracking-modal-close {
  width: 2rem;
  height: 2rem;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  background: #fff;
  color: #6b7280;
  font-size: 1.25rem;
  line-height: 1;
}

.tracking-modal-table-wrapper {
  overflow: auto;
  padding: 0 1rem 1rem;
}

.tracking-monthly-table-wrapper {
  position: relative;
}

.tracking-modal-table {
  width: 100%;
  border-collapse: collapse;
}

.tracking-modal-table th,
.tracking-modal-table td {
  padding: 0.65rem 0.5rem;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.875rem;
  color: #1f2937;
}

.tracking-modal-table th {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #64748b;
  background: #f8fafc;
}

.tracking-modal-table .is-right {
  text-align: right;
}

.tracking-monthly-table {
  min-width: 860px;
}

.tracking-monthly-table tbody th {
  text-align: left;
}

.tracking-monthly-loader {
  position: absolute;
  inset: 0 1rem 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 860px;
  pointer-events: none;
  background: rgba(255, 255, 255, 0.68);
}

.tracking-monthly-spinner {
  width: 2.25rem;
  height: 2.25rem;
  border: 3px solid #dbeafe;
  border-top-color: #2563eb;
  border-radius: 9999px;
  animation: trackingSpin 0.8s linear infinite;
}

::v-deep .tracking-total-row {
  background: #f8fafc !important;
  font-weight: 700;
}

@keyframes trackingSpin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1180px) {
  .employee-overview-grid,
  .employee-card-body {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .employee-space-header,
  .tracking-modal-header {
    flex-direction: column;
  }

  .tracking-modal-domain-filter {
    width: 100%;
    margin-left: 0;
  }

  .tracking-period-stats {
    justify-content: flex-start;
    width: 100%;
  }

  .tracking-period-stat {
    flex: 1 1 8rem;
  }

  .tracking-chart-panel {
    height: 18rem;
  }
}
</style>
